import React, {useState} from 'react';
import {
    Box,
    Button,
    IconButton,
    InputAdornment,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined';
import UndoIcon from '@mui/icons-material/Undo';
import CloseIcon from '@mui/icons-material/Close';
import {useSelector} from 'react-redux';
import {STUDENT_PROFILE_CONFIRMATION_TYPES} from '../../const/StudentProfileConfirmationTypes';
import ConfirmationWrapper from '../../Components/ConfirmationWrapper';
import AdmicityDialog from '../../../../../shared-components/AdmicityDialog';
import useNotification from '../../../../../utility/hooks/useNotification';
import {
    useAddMedicineBoxMutation,
    useAddStudentMedicineMutation,
    useSaveStudentMedicinesMutation,
    useDeleteMedicineBoxMutation,
    useDispenseMedicineMutation,
    useDisposeMedicineBoxMutation,
    useUndoDisposeMedicineBoxMutation,
    useGetDosageUnitsQuery,
    useGetMedicineDispensesQuery,
    useGetStudentMedicinesQuery,
    useRemoveStudentMedicineMutation,
    useUpdateMedicineBoxMutation,
    useUpdateStudentMedicineMutation
} from '../../../../../api/services/medicinesService';
import useUser from '../../../../../utility/hooks/useUser';
import {amountProblem, reorderProblem, boxButtonLabel, canRecordDose, dosesFromBox, nextBoxId, remainingProblem, doseProblem} from './medicineAtSchoolRules';
import {useGetMedicalConditionsQuery} from '../../../../../api/services/studentService';
import SearchableSelectField from './SearchableSelectField';
import {useGetProfileDescriptionQuery} from '../../../../../api/services/schoolManagementService';
import {DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS, PROFILE_SECTIONS_KEYS} from '../../../../../constants/ProfileSectionsKeys';
import formatProfileDescription from '../../../../../utility/profileDescriptionFormatter';
import {getHisHerPronoun} from '../../../../../utility/pronounUtil';
import {countInUnit, EXPIRY_DAY_LABEL, expiryDayOf, expiryDays, expiryMonthText, lastDayOfMonth, MEDICINE_BOX_MAX, MEDICINE_DIRECTIONS_MAX, MONTH_NAMES, unitName} from '../../../../../utility/medicines';

/* eslint-disable react/prop-types */

const emptyBox = {amount: '', remaining: '', day: '', month: '', year: ''};

const emptyDose = {dosesGiven: '', note: ''};

// The date and time box starts at the current time, to the minute, in the format the box reads.
const nowForInput = () => {
    const now = new Date();
    now.setSeconds(0, 0);
    return new Date(now.getTime() - (now.getTimezoneOffset() * 60000)).toISOString().slice(0, 16);
};

// Every table in the section has bordered cells and fixed column widths, so the rows line up and the
// section fits the page without scrolling sideways.
const GRID = {
    tableLayout: 'fixed',
    width: '100%',
    border: '1px solid #d1d5db',
    backgroundColor: '#ffffff',
    '& th, & td': {border: '1px solid #e5e7eb', px: 1, py: 0.5, verticalAlign: 'middle', fontSize: '0.875rem', lineHeight: 1.4},
    '& th': {fontWeight: 600},
    // Compact inputs and buttons, so each line is no taller than its text.
    '& .MuiInputBase-root': {fontSize: '0.875rem'},
    '& .MuiInputBase-input': {py: '5px'},
    '& .MuiInputBase-multiline': {py: '5px'},
    '& .MuiButton-root': {py: 0.25, minWidth: 0, fontSize: '0.8125rem'},
    '& .MuiIconButton-root': {p: 0.5},
    '& .MuiTypography-root': {fontSize: '0.875rem'}
};
const HEADER = '#e5e7eb';
const SUB_HEADER = '#f3f4f6';
const RECORD = '#f9fafb';

// A medicine is added with its first box, so the amount brought in and the expiry are recorded at once.
const emptyNewMedicine = {medicineLookupId: '', brandName: '', doseInstruction: '', dosageUnit: '', reorderLevel: '', amount: '', day: '', month: '', year: ''};

// A dose is given at a time of day, so its record shows the UK date and time.
const formatGivenAt = (value) => value
    ? new Date(value).toLocaleString('en-GB', {day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London'})
    : '';

const byName = (a, b) => a.localeCompare(b, 'en-GB', {numeric: true, sensitivity: 'base'});
const sortedOptions = (options) => [...(options ?? [])]
    .map(option => ({...option, brands: [...(option.brands ?? [])].sort((a, b) => byName(a.name, b.name))}))
    .sort((a, b) => byName(a.description, b.description));

const thisYear = new Date().getFullYear();
const YEARS = Array.from({length: 11}, (_, i) => thisYear - 1 + i);

// Medicine at School on a child's Medical tab (AD-2581). Each medicine is one row: Medicine,
// Brand name, Directions, Dosage unit, Remaining and Reorder level. Each box it holds is a line
// underneath: Doses in box, which never changes, Remaining, which goes down as doses are given, and
// Expiry, a month and year.
const MedicineAtSchool = ({readOnly, studentId, onConfirmSuccess}) => {
    const {selectedProfile, currentSchool} = useSelector(state => state.profilesInfo);
    const {data: descriptionData} = useGetProfileDescriptionQuery(PROFILE_SECTIONS_KEYS.medicineAtSchool);
    const {showErrorNotification, showSuccessNotification} = useNotification();

    const {user, account} = useUser();
    const givesDoses = user.hasElevatedAccess();

    const {data: loaded = {options: [], held: []}, isLoading, isError, refetch} = useGetStudentMedicinesQuery(studentId);
    // Medicines and their brands are offered in alphabetical order, with strengths in number order.
    const data = {...loaded, options: sortedOptions(loaded.options)};
    // The same query the Medical Conditions section reads, so a saved answer shows or hides this
    // section straight away.
    const {data: medicalInfo, isLoading: isConditionLoading, refetch: refetchMedicalInfo} = useGetMedicalConditionsQuery(studentId);
    const {data: units = []} = useGetDosageUnitsQuery();
    const {data: dispenses = [], refetch: refetchDispenses} = useGetMedicineDispensesQuery(studentId, {skip: !givesDoses});

    const [addStudentMedicine] = useAddStudentMedicineMutation();
    const [saveStudentMedicines] = useSaveStudentMedicinesMutation();
    const [updateStudentMedicine] = useUpdateStudentMedicineMutation();
    const [removeStudentMedicine] = useRemoveStudentMedicineMutation();
    const [addBox] = useAddMedicineBoxMutation();
    const [updateBox] = useUpdateMedicineBoxMutation();
    const [disposeBox] = useDisposeMedicineBoxMutation();
    const [undoDisposeBox] = useUndoDisposeMedicineBoxMutation();
    const [deleteBox] = useDeleteMedicineBoxMutation();
    const [dispenseMedicine] = useDispenseMedicineMutation();

    const [addMedicineOpen, setAddMedicineOpen] = useState(false);
    const [newMedicine, setNewMedicine] = useState(emptyNewMedicine);
    const [boxDialog, setBoxDialog] = useState(null);
    const [boxForm, setBoxForm] = useState(emptyBox);
    const [deleteBoxDialog, setDeleteBoxDialog] = useState(null);
    const [disposeBoxDialog, setDisposeBoxDialog] = useState(null);
    // Changes save as they are made. Once the section has been confirmed, a change turns Save on so it
    // can be confirmed again, as the other sections on this tab work.
    const [changedSinceConfirm, setChangedSinceConfirm] = useState(false);
    const [removeDialog, setRemoveDialog] = useState(null);
    // Each box's dispense record is closed until View dispense record is pressed.
    const [openRecords, setOpenRecords] = useState([]);
    const [doseEntry, setDoseEntry] = useState({...emptyDose, boxId: null});

    // A child holds one line per medicine, brand and unit, so a medicine already held can be added again
    // in another brand or unit. The server turns back the same line twice.
    const addable = data.options;
    const chosenOption = data.options.find(option => option.id === newMedicine.medicineLookupId);
    const count = (amount, unitKey) => countInUnit(Number(amount ?? 0), unitKey, units);

    // Returns whether it saved, so a refused dialog stays open with what the person typed.
    const run = async (action, message) => {
        try {
            await action().unwrap();
            await refetch();
            setChangedSinceConfirm(true);

            if (message) {
                showSuccessNotification(message);
            }

            return true;
        } catch (error) {
            showErrorNotification(error?.data?.validationErrors?.[0]?.errorMessage ??
                error?.data?.title ??
                'That could not be saved');

            return false;
        }
    };

    const saveMedicine = (medicine, changes) => run(() => updateStudentMedicine({
        studentMedicineId: medicine.id,
        medicineLookupId: medicine.medicineLookupId,
        brandName: medicine.brandName,
        doseInstruction: medicine.doseInstruction,
        dosageUnit: medicine.dosageUnit,
        reorderLevel: medicine.reorderLevel,
        ...changes
    }));

    // A reorder level is blank or a whole number from 0 to 9,999. Anything else, or a level the
    // server refuses, is put back as it was.
    const saveReorderLevel = async (medicine, input) => {
        const typed = input.value.trim();
        const level = typed === '' ? null : Number(typed);

        if (level === (medicine.reorderLevel ?? null)) {
            return;
        }

        const problem = reorderProblem(typed);

        if (problem) {
            input.value = medicine.reorderLevel ?? '';
            showErrorNotification(problem);

            return;
        }

        if (!await saveMedicine(medicine, {reorderLevel: level})) {
            input.value = medicine.reorderLevel ?? '';
        }
    };

    const handleAddMedicine = async () => {
        // The medicine and its first box are saved one after the other, as one action for run().
        const addWithFirstBox = async () => {
            const studentMedicineId = await addStudentMedicine({
                studentId,
                medicineLookupId: newMedicine.medicineLookupId,
                brandName: newMedicine.brandName || null,
                doseInstruction: newMedicine.doseInstruction,
                dosageUnit: newMedicine.dosageUnit || null,
                reorderLevel: newMedicine.reorderLevel === '' ? null : Number(newMedicine.reorderLevel)
            }).unwrap();

            return addBox({
                studentMedicineId,
                numberOfItems: 1,
                dosesPerItem: Number(newMedicine.amount),
                expiryDate: lastDayOfMonth(Number(newMedicine.year), Number(newMedicine.month)),
                expiryDay: newMedicine.day ? Number(newMedicine.day) : null
            }).unwrap();
        };
        const saved = await run(() => ({unwrap: addWithFirstBox}));

        if (saved) {
            setAddMedicineOpen(false);
            setNewMedicine(emptyNewMedicine);
        }
    };

    const chooseNewMedicine = (medicineLookupId) => {
        const option = data.options.find(o => o.id === medicineLookupId);
        setNewMedicine({
            ...newMedicine,
            medicineLookupId,
            brandName: '',
            dosageUnit: option?.dosageUnit ?? '',
            amount: option?.packAmount ? String(option.packAmount) : newMedicine.amount
        });
    };

    const chooseNewBrand = (brandName) => {
        const brand = (chosenOption?.brands ?? []).find(b => b.name === brandName);
        setNewMedicine({
            ...newMedicine,
            brandName,
            dosageUnit: brand?.dosageUnit ?? chosenOption?.dosageUnit ?? newMedicine.dosageUnit,
            amount: (brand?.packAmount ?? chosenOption?.packAmount) ? String(brand?.packAmount ?? chosenOption?.packAmount) : newMedicine.amount
        });
    };

    const handleSaveBox = async () => {
        const amount = Number(boxForm.amount);
        const payload = {
            numberOfItems: 1,
            dosesPerItem: amount,
            expiryDate: boxForm.month && boxForm.year ? lastDayOfMonth(Number(boxForm.year), Number(boxForm.month)) : null,
            expiryDay: boxForm.month && boxForm.year && boxForm.day ? Number(boxForm.day) : null
        };

        const saved = boxDialog.boxId
            ? await run(() => updateBox({
                boxId: boxDialog.boxId,
                ...payload,
                dosesRemaining: Number(boxForm.remaining)
            }))
            : await run(() => addBox({studentMedicineId: boxDialog.studentMedicineId, ...payload}));

        if (!saved) {
            return;
        }

        setBoxDialog(null);
        setBoxForm(emptyBox);
    };

    const handleGiveDose = async (medicine, entry) => {
        const description = medicine.brandName ? `${medicine.description} (${medicine.brandName})` : medicine.description;
        const saved = await run(
            () => dispenseMedicine({
                studentMedicineId: medicine.id,
                dosesGiven: Number(entry.dosesGiven),
                note: entry.note,
                givenAt: entry.givenAt ? new Date(entry.givenAt).toISOString() : null
            }),
            `${count(entry.dosesGiven, medicine.dosageUnit)} dispensed from ${description}`);

        if (!saved) {
            return;
        }

        await refetchDispenses();
        setDoseEntry({...emptyDose, boxId: null});
    };

    const handleRemoveMedicine = async () => {
        const medicine = removeDialog;
        setRemoveDialog(null);
        await run(() => removeStudentMedicine(medicine.id), 'Medicine removed');
    };

    const handleDisposeBox = async () => {
        const box = disposeBoxDialog;
        setDisposeBoxDialog(null);
        await run(() => disposeBox(box.id), 'Box marked as disposed of');
    };

    const handleDeleteBox = async () => {
        await run(() => deleteBox(deleteBoxDialog.id));
        setDeleteBoxDialog(null);
    };

    const openAddBox = (medicine) => {
        const option = data.options.find(o => o.id === medicine.medicineLookupId);
        const brand = (option?.brands ?? []).find(b => b.name === medicine.brandName);
        const pack = brand?.packAmount ?? option?.packAmount;
        setBoxForm({...emptyBox, amount: pack ? String(pack) : ''});
        setBoxDialog({studentMedicineId: medicine.id, dosageUnit: medicine.dosageUnit});
    };

    const openEditBox = (medicine, box) => {
        const [year, month] = (box.expiryDate ?? '').split('-');
        setBoxForm({
            amount: String(box.broughtIn),
            remaining: String(box.dosesRemaining),
            day: expiryDayOf(box.expiryDate),
            month: month ?? '',
            year: year ?? ''
        });
        setBoxDialog({studentMedicineId: medicine.id, boxId: box.id, dosageUnit: medicine.dosageUnit});
    };

    const unitFor = (dispense) => dispense.dosageUnit ??
        data.held.find(m => m.id === dispense.studentMedicineId)?.dosageUnit;

    if (isLoading || isConditionLoading) {
        return '';
    }

    return (
        <>
            <ConfirmationWrapper
                readonly={readOnly}
                confirmData={{
                    confirmMessage: `I confirm that the medicines held at school for ${selectedProfile.name} ${selectedProfile.surname} are correct, and that ${currentSchool.name} may give them as recorded`,
                    confirmedAt: medicalInfo?.medicinesConfirmedAt,
                    confirmedBy: medicalInfo?.medicinesConfirmedBy,
                    enableSaveButton: changedSinceConfirm,
                    confirmOnSave: true,
                    enableConfirmButton: data.held.length > 0,
                    studentId,
                    type: STUDENT_PROFILE_CONFIRMATION_TYPES?.Medicines,
                    handleSave: async () => saveStudentMedicines(studentId).unwrap(),
                    onConfirmSuccess: async () => {
                        await onConfirmSuccess();
                        await refetch();
                        await refetchMedicalInfo();
                        setChangedSinceConfirm(false);
                    },
                    confirmButtonText: 'Confirm Medicine at School'
                }}
            >
                <Typography variant="h5" pb={1} data-testid="section-medicine-at-school">
                    Medicine at School
                </Typography>
                {/* Blank unless the school has written its own words for the section in School Settings. */}
                {Boolean(descriptionData?.value ?? DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS[PROFILE_SECTIONS_KEYS.medicineAtSchool]) && (
                    <Typography variant="body1" pb={2}>
                        {formatProfileDescription(
                            descriptionData?.value ??
                            DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS[PROFILE_SECTIONS_KEYS.medicineAtSchool],
                            {
                                schoolName: currentSchool?.name,
                                studentName: selectedProfile.name,
                                hisHer: getHisHerPronoun(selectedProfile.gender)
                            })}
                    </Typography>
                )}

                {/* A failed load must never read as "no medicines", or staff would think none are held. */}
                {isError && (
                    <Typography variant="body2" color="error" pb={2} data-testid="medicine-at-school-load-error">
                        The medicines held at school could not be loaded. Please refresh the page.
                    </Typography>
                )}

                {!isError && data.held.length === 0 && (
                    <Typography variant="body2" color="text.secondary" pb={2}>
                        {currentSchool?.name ?? 'The school'} does not hold any medicines for {selectedProfile.name}.
                    </Typography>
                )}

                {data.held.map((medicine) => {
                    const option = data.options.find(o => o.id === medicine.medicineLookupId);
                    const nextBox = nextBoxId(medicine.boxes);
                    return (
                        <Box key={medicine.id} data-testid={`medicine-${medicine.id}`} sx={{mb: 1.5}}>
                            <Table size="small" sx={GRID}>
                                <colgroup>
                                    <col style={{width: '15%'}}/>
                                    <col style={{width: '15%'}}/>
                                    <col style={{width: '22%'}}/>
                                    <col style={{width: '12%'}}/>
                                    <col style={{width: '9%'}}/>
                                    <col style={{width: '8%'}}/>
                                    {!readOnly && <col style={{width: '19%'}}/>}
                                </colgroup>
                                <TableHead>
                                    <TableRow sx={{backgroundColor: HEADER}}>
                                        <TableCell>Medicine</TableCell>
                                        <TableCell>Brand name</TableCell>
                                        <TableCell>Directions</TableCell>
                                        <TableCell>Dosage unit</TableCell>
                                        <TableCell>Remaining</TableCell>
                                        <TableCell>Reorder</TableCell>
                                        {!readOnly && <TableCell/>}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell data-testid={`medicine-${medicine.id}-name`}>
                                            {readOnly
                                                ? <Typography fontWeight={600}>{medicine.description}</Typography>
                                                : (
                                                    <SearchableSelectField
                                                        size="small"
                                                        fullWidth
                                                        value={medicine.medicineLookupId}
                                                        searchTestId={`medicine-${medicine.id}-medicine-search`}
                                                        inputProps={{'data-testid': `medicine-${medicine.id}-medicine-select`}}
                                                        onChange={(event) => {
                                                            // Another medicine has its own brands, so the brand is cleared and its unit is used.
                                                            const chosen = data.options.find(o => o.id === event.target.value);
                                                            saveMedicine(medicine, {
                                                                medicineLookupId: event.target.value,
                                                                brandName: null,
                                                                dosageUnit: chosen?.dosageUnit || medicine.dosageUnit
                                                            });
                                                        }}
                                                    >
                                                        {data.options.map(o => (
                                                            <MenuItem key={o.id} value={o.id} data-search={(o.brands ?? []).map(b => b.name).join(' ')}>
                                                                {o.description}
                                                            </MenuItem>
                                                        ))}
                                                        {!data.options.some(o => o.id === medicine.medicineLookupId) && (
                                                            <MenuItem value={medicine.medicineLookupId}>{medicine.description}</MenuItem>
                                                        )}
                                                    </SearchableSelectField>
                                                )}
                                        </TableCell>
                                        <TableCell data-testid={`medicine-${medicine.id}-brand`}>
                                            {readOnly
                                                ? medicine.brandName
                                                : (
                                                    <SearchableSelectField
                                                        size="small"
                                                        fullWidth
                                                        SelectProps={{displayEmpty: true, renderValue: (value) => value || 'No brand'}}
                                                        value={medicine.brandName ?? ''}
                                                        searchTestId={`medicine-${medicine.id}-brand-search`}
                                                        inputProps={{'data-testid': `medicine-${medicine.id}-brand-select`}}
                                                        onChange={(event) => {
                                                            // A brand brings its own dosage unit, which can still be changed.
                                                            const brand = (option?.brands ?? []).find(b => b.name === event.target.value);
                                                            saveMedicine(medicine, {
                                                                brandName: event.target.value || null,
                                                                ...(brand?.dosageUnit ? {dosageUnit: brand.dosageUnit} : {})
                                                            });
                                                        }}
                                                    >
                                                        <MenuItem value="">No brand</MenuItem>
                                                        {(option?.brands ?? []).map(brand => (
                                                            <MenuItem key={brand.name} value={brand.name}>{brand.name}</MenuItem>
                                                        ))}
                                                        {medicine.brandName && !(option?.brands ?? []).some(b => b.name === medicine.brandName) && (
                                                            <MenuItem value={medicine.brandName}>{medicine.brandName}</MenuItem>
                                                        )}
                                                    </SearchableSelectField>
                                                )}
                                        </TableCell>
                                        <TableCell sx={{overflowWrap: 'anywhere'}}>
                                            {readOnly
                                                ? medicine.doseInstruction
                                                : (
                                                    <TextField
                                                        size="small"
                                                        fullWidth
                                                        multiline
                                                        data-testid={`medicine-${medicine.id}-dose`}
                                                        inputProps={{maxLength: MEDICINE_DIRECTIONS_MAX}}
                                                        defaultValue={medicine.doseInstruction ?? ''}
                                                        onChange={() => setChangedSinceConfirm(true)}
                                                        onBlur={(event) => event.target.value !== (medicine.doseInstruction ?? '') &&
                                                            saveMedicine(medicine, {doseInstruction: event.target.value})}
                                                    />
                                                )}
                                        </TableCell>
                                        <TableCell>
                                            {readOnly
                                                ? unitName(medicine.dosageUnit, units)
                                                : (
                                                    <TextField
                                                        select
                                                        size="small"
                                                        fullWidth
                                                        value={medicine.dosageUnit ?? ''}
                                                        inputProps={{'data-testid': `medicine-${medicine.id}-unit`}}
                                                        onChange={(event) => saveMedicine(medicine, {dosageUnit: event.target.value || null})}
                                                    >
                                                        {units.map(unit => (
                                                            <MenuItem key={unit.key} value={unit.key}>{unitName(unit.key, units)}</MenuItem>
                                                        ))}
                                                    </TextField>
                                                )}
                                        </TableCell>
                                        <TableCell data-testid={`medicine-${medicine.id}-doses-held`}>
                                            <Typography fontWeight={600}>{medicine.dosesHeld}</Typography>
                                            {medicine.belowReorderLevel && <Typography variant="body2" color="warning.main" fontWeight={600}>Reorder</Typography>}
                                        </TableCell>
                                        <TableCell>
                                            {readOnly
                                                ? medicine.reorderLevel
                                                : (
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        fullWidth
                                                        data-testid={`medicine-${medicine.id}-reorder`}
                                                        inputProps={{min: 0, step: 1}}
                                                        defaultValue={medicine.reorderLevel ?? ''}
                                                        onChange={() => setChangedSinceConfirm(true)}
                                                        onBlur={(event) => saveReorderLevel(medicine, event.target)}
                                                    />
                                                )}
                                        </TableCell>
                                        {!readOnly && (
                                            <TableCell align="right">
                                                <Stack direction="row" justifyContent="flex-end" alignItems="center">
                                                    <Button
                                                        size="small"
                                                        sx={{whiteSpace: 'nowrap'}}
                                                        data-testid={`medicine-${medicine.id}-add-box`}
                                                        onClick={() => openAddBox(medicine)}
                                                    >
                                                        {boxButtonLabel(medicine.boxes.length)}
                                                    </Button>
                                                    <Tooltip title="Remove this medicine">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            aria-label="Remove this medicine"
                                                            data-testid={`medicine-${medicine.id}-remove`}
                                                            onClick={() => setRemoveDialog(medicine)}
                                                        >
                                                            <CloseIcon fontSize="small"/>
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                </TableBody>
                            </Table>

                            {medicine.boxes.length > 0 && (
                                <Table size="small" sx={{...GRID, borderTop: 0}} data-testid={`medicine-${medicine.id}-boxes`}>
                                    {/* Expiry first, so each line reads as a separate box of the same medicine. */}
                                    <colgroup>
                                        <col style={{width: '15%'}}/>
                                        <col style={{width: '15%'}}/>
                                        {givesDoses && <col style={{width: '22%'}}/>}
                                        <col style={{width: givesDoses ? '21%' : '43%'}}/>
                                        <col style={{width: '27%'}}/>
                                    </colgroup>
                                    <TableHead>
                                        <TableRow sx={{backgroundColor: SUB_HEADER}}>
                                            <TableCell>Expiry</TableCell>
                                            <TableCell>Doses in box</TableCell>
                                            {givesDoses && <TableCell>Given</TableCell>}
                                            <TableCell>Remaining</TableCell>
                                            <TableCell/>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {medicine.boxes.map((box) => {
                                            const past = Boolean(box.disposedAt) || Boolean(box.emptiedAt) || box.isExpired;
                                            // An expired box cannot be dispensed from, so its line reads in red.
                                            const expired = !box.disposedAt && !box.emptiedAt && box.isExpired;
                                            const {doses, given} = dosesFromBox(dispenses, box.id);
                                            const recordOpen = openRecords.includes(box.id);
                                            // Doses come from the box that expires first, so a new dose is logged in its record.
                                            const dispensesFrom = givesDoses && !readOnly && box.id === nextBox;
                                            const entry = doseEntry.boxId === box.id ? doseEntry : {...emptyDose, boxId: box.id, givenAt: nowForInput()};
                                            const problem = doseProblem(entry.dosesGiven, medicine.dosesHeld);

                                            return (
                                                <React.Fragment key={box.id}>
                                                    <TableRow
                                                        data-testid={`box-${box.id}`}
                                                        sx={{opacity: past && !expired ? 0.55 : 1, '& td': expired ? {color: 'error.main'} : {}}}
                                                    >
                                                        <TableCell data-testid={`box-${box.id}-expiry`}>
                                                            {expiryMonthText(box.expiryDate) || 'No expiry date'}
                                                        </TableCell>
                                                        <TableCell data-testid={`box-${box.id}-brought-in`}>{box.broughtIn}</TableCell>
                                                        {givesDoses && <TableCell data-testid={`box-${box.id}-given`}>{given}</TableCell>}
                                                        <TableCell data-testid={`box-${box.id}-remaining`}>{box.dosesRemaining}</TableCell>
                                                        <TableCell align="right">
                                                            <Stack direction="row" justifyContent="flex-end" alignItems="center">
                                                                {givesDoses && (
                                                                    <Button
                                                                        size="small"
                                                                        sx={{whiteSpace: 'nowrap'}}
                                                                        data-testid={`box-${box.id}-record`}
                                                                        onClick={() => setOpenRecords(recordOpen
                                                                            ? openRecords.filter(id => id !== box.id)
                                                                            : [...openRecords, box.id])}
                                                                    >
                                                                        Dispensing Record
                                                                    </Button>
                                                                )}
                                                                {!readOnly && (
                                                                    <>
                                                                        <Tooltip title="Correct this box">
                                                                            <IconButton
                                                                                size="small"
                                                                                data-testid={`box-${box.id}-edit`}
                                                                                onClick={() => openEditBox(medicine, box)}
                                                                            >
                                                                                <EditIcon fontSize="small"/>
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                        {!box.disposedAt && (
                                                                            <Tooltip title="Mark as disposed of">
                                                                                <IconButton
                                                                                    size="small"
                                                                                    data-testid={`box-${box.id}-dispose`}
                                                                                    onClick={() => setDisposeBoxDialog(box)}
                                                                                >
                                                                                    <DeleteSweepOutlinedIcon fontSize="small"/>
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        )}
                                                                        {box.disposedAt && (
                                                                            <Tooltip title="Put back in use">
                                                                                <IconButton
                                                                                    size="small"
                                                                                    data-testid={`box-${box.id}-undo-dispose`}
                                                                                    onClick={() => run(() => undoDisposeBox(box.id), 'Box put back in use')}
                                                                                >
                                                                                    <UndoIcon fontSize="small"/>
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        )}
                                                                        <Tooltip title="Delete a box added by mistake">
                                                                            <IconButton
                                                                                size="small"
                                                                                data-testid={`box-${box.id}-delete`}
                                                                                onClick={() => setDeleteBoxDialog(box)}
                                                                            >
                                                                                <DeleteIcon fontSize="small"/>
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    </>
                                                                )}
                                                            </Stack>
                                                        </TableCell>
                                                    </TableRow>
                                                    {givesDoses && recordOpen && (
                                                        <TableRow data-testid={`box-${box.id}-doses`}>
                                                            <TableCell colSpan={givesDoses ? 5 : 4} sx={{backgroundColor: RECORD, p: 1.5}}>
                                                                <Table size="small" sx={{...GRID, backgroundColor: '#ffffff'}}>
                                                                    <colgroup>
                                                                        <col style={{width: '24%'}}/>
                                                                        <col style={{width: '18%'}}/>
                                                                        <col style={{width: '18%'}}/>
                                                                        <col style={{width: '28%'}}/>
                                                                        <col style={{width: '12%'}}/>
                                                                    </colgroup>
                                                                    <TableHead>
                                                                        <TableRow sx={{backgroundColor: SUB_HEADER}}>
                                                                            <TableCell>Date and time given</TableCell>
                                                                            <TableCell>Amount given</TableCell>
                                                                            <TableCell>Given by</TableCell>
                                                                            <TableCell>Note</TableCell>
                                                                            <TableCell/>
                                                                        </TableRow>
                                                                    </TableHead>
                                                                    <TableBody>
                                                                        {dispensesFrom && (
                                                                            <TableRow data-testid={`box-${box.id}-new-dose`}>
                                                                                <TableCell>
                                                                                    <TextField
                                                                                        size="small"
                                                                                        type="datetime-local"
                                                                                        fullWidth
                                                                                        inputProps={{max: nowForInput(), 'data-testid': 'dose-given-at'}}
                                                                                        value={entry.givenAt}
                                                                                        onChange={(event) => setDoseEntry({...entry, givenAt: event.target.value})}
                                                                                    />
                                                                                </TableCell>
                                                                                <TableCell>
                                                                                    <TextField
                                                                                        size="small"
                                                                                        type="number"
                                                                                        fullWidth
                                                                                        InputProps={{
                                                                                            // The unit reads in black beside the number, for example "2 puffs".
                                                                                            endAdornment: (
                                                                                                <InputAdornment position="end" sx={{'& p': {color: 'text.primary'}}}>
                                                                                                    {count(2, medicine.dosageUnit).replace(/^2 /, '')}
                                                                                                </InputAdornment>
                                                                                            )
                                                                                        }}
                                                                                        inputProps={{min: 1, step: 1, 'data-testid': 'dose-given'}}
                                                                                        error={Boolean(problem)}
                                                                                        helperText={problem}
                                                                                        value={entry.dosesGiven}
                                                                                        onChange={(event) => setDoseEntry({...entry, dosesGiven: event.target.value})}
                                                                                    />
                                                                                </TableCell>
                                                                                <TableCell>{`${account?.firstName ?? ''} ${account?.lastName ?? ''}`.trim()}</TableCell>
                                                                                <TableCell>
                                                                                    <TextField
                                                                                        size="small"
                                                                                        fullWidth
                                                                                        inputProps={{maxLength: 1000, 'data-testid': 'dose-note'}}
                                                                                        value={entry.note}
                                                                                        onChange={(event) => setDoseEntry({...entry, note: event.target.value})}
                                                                                    />
                                                                                </TableCell>
                                                                                <TableCell align="right">
                                                                                    <Button
                                                                                        size="small"
                                                                                        variant="contained"
                                                                                        data-testid={`medicine-${medicine.id}-give-dose`}
                                                                                        disabled={!canRecordDose(entry.dosesGiven, medicine.dosesHeld) || !entry.givenAt || entry.givenAt > nowForInput()}
                                                                                        onClick={() => handleGiveDose(medicine, entry)}
                                                                                    >
                                                                                        Dispense
                                                                                    </Button>
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        )}
                                                                        {doses.map(dose => (
                                                                            <TableRow key={dose.id} data-testid={`dispense-${dose.id}`}>
                                                                                <TableCell>{formatGivenAt(dose.givenAt)}</TableCell>
                                                                                <TableCell>{count(dose.dosesGiven, unitFor(dose))}</TableCell>
                                                                                <TableCell>{dose.givenBy}</TableCell>
                                                                                <TableCell sx={{overflowWrap: 'anywhere'}}>{dose.note}</TableCell>
                                                                                <TableCell/>
                                                                            </TableRow>
                                                                        ))}
                                                                        {!dispensesFrom && doses.length === 0 && (
                                                                            <TableRow>
                                                                                <TableCell colSpan={5}>Doses have not been given from this box.</TableCell>
                                                                            </TableRow>
                                                                        )}
                                                                    </TableBody>
                                                                </Table>
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            )}
                        </Box>
                    );
                })}

                {!readOnly && addMedicineOpen && (
                    // A new medicine is a new row, with its first box underneath, saved together.
                    <Box data-testid="new-medicine-row" sx={{mb: 2}}>
                        <Box>
                            <Table size="small" sx={GRID}>
                                <TableHead>
                                    <TableRow sx={{backgroundColor: HEADER}}>
                                        <TableCell>Medicine</TableCell>
                                        <TableCell>Brand name</TableCell>
                                        <TableCell>Directions</TableCell>
                                        <TableCell>Dosage unit</TableCell>
                                        <TableCell>Reorder</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell>
                                            <SearchableSelectField
                                                size="small"
                                                fullWidth
                                                searchTestId="medicine-choice-search"
                                                SelectProps={{displayEmpty: true, renderValue: (value) => addable.find(o => o.id === value)?.description ?? 'Choose'}}
                                                value={newMedicine.medicineLookupId}
                                                inputProps={{'data-testid': 'medicine-choice'}}
                                                onChange={(event) => chooseNewMedicine(event.target.value)}
                                            >
                                                {addable.map(option => (
                                                    <MenuItem
                                                        key={option.id}
                                                        value={option.id}
                                                        data-testid={`medicine-option-${option.id}`}
                                                        data-search={(option.brands ?? []).map(b => b.name).join(' ')}
                                                    >
                                                        {option.description}
                                                    </MenuItem>
                                                ))}
                                            </SearchableSelectField>
                                        </TableCell>
                                        <TableCell>
                                            <SearchableSelectField
                                                size="small"
                                                fullWidth
                                                searchTestId="medicine-brand-choice-search"
                                                disabled={!chosenOption}
                                                SelectProps={{displayEmpty: true, renderValue: (value) => value || 'No brand'}}
                                                value={newMedicine.brandName}
                                                inputProps={{'data-testid': 'medicine-brand-choice'}}
                                                onChange={(event) => chooseNewBrand(event.target.value)}
                                            >
                                                <MenuItem value="">No brand</MenuItem>
                                                {(chosenOption?.brands ?? []).map(brand => (
                                                    <MenuItem key={brand.name} value={brand.name}>{brand.name}</MenuItem>
                                                ))}
                                            </SearchableSelectField>
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                size="small"
                                                fullWidth
                                                multiline
                                                inputProps={{maxLength: MEDICINE_DIRECTIONS_MAX, 'data-testid': 'medicine-directions'}}
                                                value={newMedicine.doseInstruction}
                                                onChange={(event) => setNewMedicine({...newMedicine, doseInstruction: event.target.value})}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                select
                                                size="small"
                                                fullWidth
                                                value={newMedicine.dosageUnit}
                                                inputProps={{'data-testid': 'medicine-unit-choice'}}
                                                onChange={(event) => setNewMedicine({...newMedicine, dosageUnit: event.target.value})}
                                            >
                                                {units.map(unit => <MenuItem key={unit.key} value={unit.key}>{unitName(unit.key, units)}</MenuItem>)}
                                            </TextField>
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                size="small"
                                                type="number"
                                                fullWidth
                                                inputProps={{min: 0, max: 9999, step: 1, 'data-testid': 'medicine-reorder'}}
                                                value={newMedicine.reorderLevel}
                                                error={Boolean(reorderProblem(newMedicine.reorderLevel))}
                                                helperText={reorderProblem(newMedicine.reorderLevel)}
                                                onChange={(event) => setNewMedicine({...newMedicine, reorderLevel: event.target.value})}
                                            />
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </Box>
                        <Box>
                            <Table size="small" sx={{...GRID, borderTop: 0}}>
                                <TableHead>
                                    <TableRow sx={{backgroundColor: SUB_HEADER}}>
                                        <TableCell>Doses in box</TableCell>
                                        <TableCell>Expiry</TableCell>
                                        <TableCell/>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell>
                                            <TextField
                                                size="small"
                                                type="number"
                                                sx={{width: 120}}
                                                inputProps={{min: 1, max: MEDICINE_BOX_MAX, step: 1, 'data-testid': 'medicine-first-box-amount'}}
                                                error={newMedicine.amount !== '' && Boolean(amountProblem(newMedicine.amount))}
                                                helperText={newMedicine.amount !== '' ? amountProblem(newMedicine.amount) : ''}
                                                value={newMedicine.amount}
                                                onChange={(event) => setNewMedicine({...newMedicine, amount: event.target.value})}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" gap={1}>
                                                <TextField
                                                    select
                                                    size="small"
                                                    sx={{minWidth: 80}}
                                                    title={EXPIRY_DAY_LABEL}
                                                    SelectProps={{displayEmpty: true, renderValue: (value) => value || 'Day'}}
                                                    value={newMedicine.day}
                                                    inputProps={{'data-testid': 'medicine-first-box-day', 'aria-label': EXPIRY_DAY_LABEL}}
                                                    onChange={(event) => setNewMedicine({...newMedicine, day: event.target.value})}
                                                >
                                                    <MenuItem value="">Day</MenuItem>
                                                    {expiryDays(newMedicine.year, newMedicine.month).map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                                                </TextField>
                                                <TextField
                                                    select
                                                    size="small"
                                                    fullWidth
                                                    SelectProps={{displayEmpty: true, renderValue: (value) => (value ? MONTH_NAMES[Number(value) - 1] : 'Month')}}
                                                    value={newMedicine.month}
                                                    inputProps={{'data-testid': 'medicine-first-box-month'}}
                                                    onChange={(event) => setNewMedicine({...newMedicine, month: event.target.value, day: Number(newMedicine.day) > expiryDays(newMedicine.year || 2000, event.target.value).length ? '' : newMedicine.day})}
                                                >
                                                    {MONTH_NAMES.map((name, i) => (
                                                        <MenuItem key={name} value={String(i + 1).padStart(2, '0')}>{name}</MenuItem>
                                                    ))}
                                                </TextField>
                                                <TextField
                                                    select
                                                    size="small"
                                                    sx={{minWidth: 100}}
                                                    SelectProps={{displayEmpty: true, renderValue: (value) => value || 'Year'}}
                                                    value={newMedicine.year}
                                                    inputProps={{'data-testid': 'medicine-first-box-year'}}
                                                    onChange={(event) => setNewMedicine({...newMedicine, year: event.target.value, day: Number(newMedicine.day) > expiryDays(event.target.value, newMedicine.month || 1).length ? '' : newMedicine.day})}
                                                >
                                                    {YEARS.map(y => <MenuItem key={y} value={String(y)}>{y}</MenuItem>)}
                                                </TextField>
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="right" sx={{whiteSpace: 'nowrap'}}>
                                            <Button
                                                size="small"
                                                data-testid="new-medicine-cancel"
                                                onClick={() => {
                                                    setAddMedicineOpen(false);
                                                    setNewMedicine(emptyNewMedicine);
                                                }}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                data-testid="new-medicine-save"
                                                disabled={!newMedicine.medicineLookupId || !newMedicine.dosageUnit ||
                                                    Boolean(amountProblem(newMedicine.amount)) || Boolean(reorderProblem(newMedicine.reorderLevel)) ||
                                                    !newMedicine.month || !newMedicine.year}
                                                onClick={handleAddMedicine}
                                            >
                                                Save
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </Box>
                    </Box>
                )}

                {!readOnly && !addMedicineOpen && addable.length > 0 && (
                    <Button
                        variant="outlined"
                        data-testid="add-medicine-at-school"
                        onClick={() => setAddMedicineOpen(true)}
                    >
                        Add a medicine
                    </Button>
                )}

            </ConfirmationWrapper>

            <AdmicityDialog
                open={Boolean(boxDialog)}
                title={boxDialog?.boxId ? 'Correct this box' : 'Add a box'}
                handleClose={() => setBoxDialog(null)}
                actions={[
                    {label: 'Cancel', onClick: () => setBoxDialog(null)},
                    {
                        label: 'Save',
                        onClick: handleSaveBox,
                        disabled: Boolean(amountProblem(boxForm.amount)) ||
                            (Boolean(boxDialog?.boxId) && Boolean(remainingProblem(boxForm))) ||
                            !boxForm.month || !boxForm.year
                    }
                ]}
            >
                <Stack gap={2} sx={{mt: 1}}>
                    <TextField
                        label={`Doses in box (${count(2, boxDialog?.dosageUnit).replace(/^2 /, '')})`}
                        type="number"
                        fullWidth
                        data-testid="box-amount"
                        inputProps={{min: 1, max: MEDICINE_BOX_MAX, step: 1}}
                        error={boxForm.amount !== '' && Boolean(amountProblem(boxForm.amount))}
                        helperText={boxForm.amount !== '' ? amountProblem(boxForm.amount) : ''}
                        value={boxForm.amount}
                        onChange={(event) => setBoxForm({...boxForm, amount: event.target.value})}
                    />
                    {boxDialog?.boxId && (
                        <TextField
                            label="Remaining"
                            type="number"
                            fullWidth
                            data-testid="box-doses-remaining"
                            inputProps={{min: 0, step: 1}}
                            error={Boolean(remainingProblem(boxForm))}
                            helperText={remainingProblem(boxForm)}
                            value={boxForm.remaining}
                            onChange={(event) => setBoxForm({...boxForm, remaining: event.target.value})}
                        />
                    )}
                    <TextField
                        select
                        fullWidth
                        label={EXPIRY_DAY_LABEL}
                        data-testid="box-expiry-day"
                        value={boxForm.day}
                        onChange={(event) => setBoxForm({...boxForm, day: event.target.value})}
                    >
                        <MenuItem value="">&nbsp;</MenuItem>
                        {expiryDays(boxForm.year, boxForm.month).map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                    </TextField>
                    <Stack direction="row" gap={2}>
                        <TextField
                            select
                            fullWidth
                            label="Expiry month"
                            data-testid="box-expiry-month"
                            value={boxForm.month}
                            onChange={(event) => setBoxForm({...boxForm, month: event.target.value, day: Number(boxForm.day) > expiryDays(boxForm.year || 2000, event.target.value).length ? '' : boxForm.day})}
                        >
                            {MONTH_NAMES.map((name, i) => (
                                <MenuItem key={name} value={String(i + 1).padStart(2, '0')}>{name}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            fullWidth
                            label="Expiry year"
                            data-testid="box-expiry-year"
                            value={boxForm.year}
                            onChange={(event) => setBoxForm({...boxForm, year: event.target.value})}
                        >
                            {YEARS.map(y => <MenuItem key={y} value={String(y)}>{y}</MenuItem>)}
                        </TextField>
                    </Stack>
                </Stack>
            </AdmicityDialog>

            <AdmicityDialog
                open={Boolean(removeDialog)}
                title="Remove this medicine"
                handleClose={() => setRemoveDialog(null)}
                actions={[
                    {label: 'Cancel', onClick: () => setRemoveDialog(null)},
                    {label: 'Remove', onClick: handleRemoveMedicine}
                ]}
            >
                <Typography data-testid="medicine-remove-warning">
                    {removeDialog
                        ? `Remove ${removeDialog.description}${removeDialog.brandName ? ` (${removeDialog.brandName})` : ''} and its boxes from ${selectedProfile.name}'s Medicine at School? A medicine that doses have been given from stays on the record.`
                        : ''}
                </Typography>
            </AdmicityDialog>

            <AdmicityDialog
                open={Boolean(disposeBoxDialog)}
                title="Mark this box as disposed of"
                handleClose={() => setDisposeBoxDialog(null)}
                actions={[
                    {label: 'Cancel', onClick: () => setDisposeBoxDialog(null)},
                    {label: 'Mark as disposed of', onClick: handleDisposeBox}
                ]}
            >
                <Typography data-testid="box-dispose-warning">
                    {disposeBoxDialog
                        ? `The ${disposeBoxDialog.dosesRemaining} left in this box will no longer count as held at school. Put back in use undoes this.`
                        : ''}
                </Typography>
            </AdmicityDialog>

            <AdmicityDialog
                open={Boolean(deleteBoxDialog)}
                title="Delete this box"
                handleClose={() => setDeleteBoxDialog(null)}
                actions={[
                    {label: 'Cancel', onClick: () => setDeleteBoxDialog(null)},
                    {label: 'Delete', onClick: handleDeleteBox}
                ]}
            >
                <Typography data-testid="box-delete-warning">
                    {deleteBoxDialog?.dispenseCount > 0
                        ? `Deleting this box also deletes the ${deleteBoxDialog.dispenseCount === 1 ? 'dose' : `${deleteBoxDialog.dispenseCount} doses`} recorded against it. `
                        : 'Doses have not been recorded against this box. '}
                    A box that has been used up or thrown away is marked as disposed of instead, which keeps the record.
                </Typography>
            </AdmicityDialog>
        </>
    );
};

export default MedicineAtSchool;
